// src/pages/AlbumCreator.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Button,
    Container,
    Heading,
    VStack,
    Stepper,
    StepIndicator,
    Step,
    StepStatus,
    StepIcon,
    StepNumber,
    StepTitle,
    StepDescription,
    StepSeparator,
    useSteps,
    Flex,
    useToast,
    useBreakpointValue,
    useColorModeValue,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalBody,
    Progress,
    Text
} from '@chakra-ui/react';
import {
    FiArrowLeft,
    FiArrowRight,
    FiCheck,
} from 'react-icons/fi';

import HeaderDashboard from '../components/dashboard/HeaderDashboard';
import Footer from '../components/Footer';
import { supabase } from '../config/supabaseClient';

import StepImageSelector from '../components/album/StepImageSelector';
import StepConfigurations from '../components/album/StepConfigurations';
import StepOrganizer from '../components/album/StepOrganizer';
import StepFinalize from '../components/album/StepFinalize';
import { generatePDF } from '../utils/album/pdfGenerator';

const AlbumCreator = () => {
    const navigate = useNavigate();
    const toast = useToast();

    // Estados
    const [selectedImages, setSelectedImages] = useState([]);
    const [albumTitle, setAlbumTitle] = useState('Meu Livro de Colorir');
    const [coverStyle, setCoverStyle] = useState('infantil');
    const [paperSize, setPaperSize] = useState('A4');
    const [orientation, setOrientation] = useState('portrait');
    const [pageLayout, setPageLayout] = useState('one_per_page');
    const [dedication, setDedication] = useState('');
    const [specialPages, setSpecialPages] = useState(['instructions', 'certificate']);

    // Adicione estados se ainda não tiver
    const [user, setUser] = useState(null);
    const [profile, setProfile] = useState(null);

    const [pdfProgress, setPdfProgress] = useState(0);
    const [showProgressModal, setShowProgressModal] = useState(false);

    // Stepper
    const { activeStep, setActiveStep } = useSteps({
        index: 0,
        count: 4,
    });

    // Estado para controlar se está gerando o PDF
    const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
    // Estado para controlar se está carregando imagens
    const [isLoadingImages, setIsLoadingImages] = useState(true);

    // Responsividade
    const headingSize = useBreakpointValue({ base: "md", md: "lg" });
    const buttonSize = useBreakpointValue({ base: "sm", md: "md" });
    const stepperOrientation = useBreakpointValue({ base: "vertical", md: "horizontal" });
    const stepperSize = useBreakpointValue({ base: "sm", md: "md" });

    // Cores
    const bgColor = useColorModeValue("white", "gray.800");
    const borderColor = useColorModeValue("gray.200", "gray.700");

    // Carregar imagens do localStorage
    useEffect(() => {
        setIsLoadingImages(true);
        try {
            const storedImages = localStorage.getItem('albumImages');
            if (storedImages) {
                const parsedImages = JSON.parse(storedImages);
                setSelectedImages(parsedImages);
            }

            const storedSettings = localStorage.getItem('albumSettings');
            if (storedSettings) {
                const settings = JSON.parse(storedSettings);
                setAlbumTitle(settings.title || 'Meu Livro de Colorir');
                setCoverStyle(settings.coverStyle || 'infantil');
                setPaperSize(settings.paperSize || 'A4');
                setOrientation(settings.orientation || 'portrait');
                setPageLayout(settings.pageLayout || 'one_per_page');
                setDedication(settings.dedication || '');
                setSpecialPages(settings.specialPages || ['instructions', 'certificate']);
            }
        } catch (error) {
            console.error('Erro ao carregar configurações:', error);
        } finally {
            setIsLoadingImages(false);
        }
    }, []);

    // No useEffect, carregue os dados do usuário
    useEffect(() => {
        const loadUserData = async () => {
            const { data: { session } } = await supabase.auth.getSession();
            if (session) {
                setUser(session.user);
                // Buscar perfil
                const { data: profileData } = await supabase
                    .from('profiles')
                    .select('*')
                    .eq('id', session.user.id)
                    .single();
                setProfile(profileData);
            }
        };
        loadUserData();
    }, []);

    // Salvar configurações no localStorage
    const saveSettings = useCallback(() => {
        localStorage.setItem('albumSettings', JSON.stringify({
            title: albumTitle,
            coverStyle,
            paperSize,
            orientation,
            pageLayout,
            dedication,
            specialPages
        }));
    }, [albumTitle, coverStyle, paperSize, orientation, pageLayout, dedication, specialPages]);

    // Usar debounce para salvar configurações
    useEffect(() => {
        const timer = setTimeout(() => {
            saveSettings();
        }, 500);

        return () => clearTimeout(timer);
    }, [saveSettings]);

    // Remover imagem
    const handleRemoveImage = (imageId) => {
        setSelectedImages(prevImages => {
            const updatedImages = prevImages.filter(img => img.id !== imageId);
            localStorage.setItem('albumImages', JSON.stringify(updatedImages));
            return updatedImages;
        });

        toast({
            title: "Imagem removida",
            status: "info",
            duration: 2000,
            isClosable: true,
        });
    };

    // Função para mover imagem
    const moveImage = (index, direction) => {
        const newImages = [...selectedImages];

        if (direction === 'up' && index > 0) {
            [newImages[index], newImages[index - 1]] = [newImages[index - 1], newImages[index]];
        } else if (direction === 'down' && index < newImages.length - 1) {
            [newImages[index], newImages[index + 1]] = [newImages[index + 1], newImages[index]];
        }

        setSelectedImages(newImages);
        localStorage.setItem('albumImages', JSON.stringify(newImages));
    };

    // Função para gerar o PDF
    // Atualizar a função handleGeneratePDF
    const handleGeneratePDF = async () => {
        if (selectedImages.length === 0) {
            toast({
                title: "Nenhuma imagem selecionada",
                description: "Adicione pelo menos uma imagem ao seu livro",
                status: "warning",
                duration: 3000,
                isClosable: true,
            });
            return;
        }

        setIsGeneratingPdf(true);
        setShowProgressModal(true); // ADICIONAR ESTA LINHA
        setPdfProgress(0); // ADICIONAR ESTA LINHA

        try {
            // Simular progresso (você pode fazer isso real se quiser)
            setPdfProgress(20);

            const pdf = await generatePDF({
                selectedImages,
                albumTitle,
                dedication,
                paperSize,
                orientation,
                pageLayout,
                specialPages,
                onProgress: (progress) => setPdfProgress(progress) // ADICIONAR CALLBACK DE PROGRESSO
            });

            setPdfProgress(80);

            const filename = albumTitle.replace(/[^a-z0-9]/gi, '_').toLowerCase() || "meu_livro_de_colorir";
            pdf.save(`${filename}.pdf`);

            setPdfProgress(100);

            toast({
                title: "PDF gerado com sucesso!",
                description: "Seu livro de colorir foi baixado",
                status: "success",
                duration: 3000,
                isClosable: true,
            });

        } catch (error) {
            console.error("Erro ao gerar PDF:", error);
            toast({
                title: "Erro ao gerar PDF",
                description: "Ocorreu um erro ao gerar seu livro. Tente novamente.",
                status: "error",
                duration: 3000,
                isClosable: true,
            });
        } finally {
            setIsGeneratingPdf(false);
            setShowProgressModal(false); // ADICIONAR ESTA LINHA
        }
    };

    // Passos do stepper
    const steps = [
        {
            title: 'Selecionar',
            description: 'Imagens selecionadas',
            content: (
                <StepImageSelector
                    selectedImages={selectedImages}
                    isLoadingImages={isLoadingImages}
                    onRemoveImage={handleRemoveImage}
                    onClearAll={() => {
                        setSelectedImages([]);
                        localStorage.setItem('albumImages', JSON.stringify([]));
                    }}
                />
            )
        },
        {
            title: 'Configurar',
            description: 'Formato e estilo',
            content: (
                <StepConfigurations
                    albumTitle={albumTitle}
                    setAlbumTitle={setAlbumTitle}
                    dedication={dedication}
                    setDedication={setDedication}
                    paperSize={paperSize}
                    setPaperSize={setPaperSize}
                    orientation={orientation}
                    setOrientation={setOrientation}
                    pageLayout={pageLayout}
                    setPageLayout={setPageLayout}
                    coverStyle={coverStyle}
                    setCoverStyle={setCoverStyle}
                />
            )
        },
        {
            title: 'Organizar',
            description: 'Ordem das páginas',
            content: (
                <StepOrganizer
                    selectedImages={selectedImages}
                    onRemoveImage={handleRemoveImage}
                    onMoveImage={moveImage}
                    specialPages={specialPages}
                    onSpecialPagesChange={setSpecialPages}
                />
            )
        },
        {
            title: 'Finalizar',
            description: 'Visualizar e baixar',
            content: (
                <StepFinalize
                    albumTitle={albumTitle}
                    selectedImages={selectedImages}
                    paperSize={paperSize}
                    orientation={orientation}
                    pageLayout={pageLayout}
                    dedication={dedication}
                    specialPages={specialPages}
                    onGeneratePDF={handleGeneratePDF}
                    isGeneratingPdf={isGeneratingPdf}
                />
            )
        },
    ];

    return (
        <Box minH="100vh" bg="gray.50">

            {/* HEADER */}
            <HeaderDashboard
                profile={profile}
                user={user}
                showCredits={true}
                showBuyButton={false}
                currentPath="album"
                onSignOut={async () => {
                    await supabase.auth.signOut();
                    navigate('/login');
                }}
                onNavigate={(action) => {
                    // Se for para o profile, navega pro dashboard com estado
                    if (action === 'profile') {
                        navigate('/dashboard', { state: { openProfile: true } });
                    } else if (action === '/dashboard') {
                        navigate('/dashboard');
                    }
                }}
            />

            <Container maxW="container.xl" py={{ base: 4, md: 8 }}>
                <VStack spacing={{ base: 4, md: 8 }} align="stretch">
                    <Flex align="center" justify="space-between" wrap="wrap" gap={2}>
                        <Button
                            leftIcon={<FiArrowLeft />}
                            variant="outline"
                            onClick={() => navigate('/dashboard')}
                            size={buttonSize}
                        >
                            Voltar à Galeria
                        </Button>
                        <Heading size={headingSize} textAlign={{ base: "center", md: "left" }} flex={{ base: "100%", md: "1" }}>
                            Criar Livro de Colorir
                        </Heading>
                    </Flex>

                    <Box
                        p={{ base: 4, md: 8 }}
                        bg={bgColor}
                        borderRadius="lg"
                        borderWidth="1px"
                        borderColor={borderColor}
                        boxShadow="md"
                    >
                        <Stepper
                            index={activeStep}
                            mb={8}
                            colorScheme="purple"
                            orientation={stepperOrientation}
                            size={stepperSize}
                        >
                            {steps.map((step, index) => (
                                <Step key={index} onClick={() => setActiveStep(index)}>
                                    <StepIndicator>
                                        <StepStatus
                                            complete={<StepIcon />}
                                            incomplete={<StepNumber />}
                                            active={<StepNumber />}
                                        />
                                    </StepIndicator>
                                    <Box flexShrink="0">
                                        <StepTitle fontSize={{ base: "sm", md: "md" }}>
                                            {step.title}
                                        </StepTitle>
                                        <StepDescription
                                            display={{ base: "none", md: "block" }}
                                            fontSize={{ base: "xs", md: "sm" }}
                                        >
                                            {step.description}
                                        </StepDescription>
                                    </Box>
                                    <StepSeparator />
                                </Step>
                            ))}
                        </Stepper>

                        <Box mb={8}>
                            {steps[activeStep].content}
                        </Box>

                        <Flex justify="space-between" mt={8} wrap="wrap" gap={4}>
                            <Button
                                leftIcon={<FiArrowLeft />}
                                onClick={() => setActiveStep(activeStep - 1)}
                                isDisabled={activeStep === 0}
                                variant="outline"
                                size={buttonSize}
                                width={{ base: "100%", sm: "auto" }}
                            >
                                Anterior
                            </Button>

                            {activeStep < steps.length - 1 ? (
                                <Button
                                    rightIcon={<FiArrowRight />}
                                    colorScheme="purple"
                                    onClick={() => setActiveStep(activeStep + 1)}
                                    isDisabled={selectedImages.length === 0}
                                    size={buttonSize}
                                    width={{ base: "100%", sm: "auto" }}
                                >
                                    Próximo
                                </Button>
                            ) : (
                                <Button
                                    rightIcon={<FiCheck />}
                                    colorScheme="green"
                                    onClick={handleGeneratePDF}
                                    isLoading={isGeneratingPdf}
                                    loadingText="Gerando..."
                                    isDisabled={selectedImages.length === 0}
                                    size={buttonSize}
                                    width={{ base: "100%", sm: "auto" }}
                                >
                                    Concluir
                                </Button>
                            )}
                        </Flex>
                    </Box>
                </VStack>
            </Container>
            {/* ADICIONAR O MODAL AQUI, ANTES DO FOOTER */}
            <Modal isOpen={showProgressModal} onClose={() => { }} closeOnOverlayClick={false}>
                <ModalOverlay />
                <ModalContent>
                    <ModalBody py={6}>
                        <VStack spacing={4}>
                            <Text>Gerando seu livro de colorir...</Text>
                            <Progress value={pdfProgress} width="100%" colorScheme="blue" />
                            <Text fontSize="sm" color="gray.500">
                                {pdfProgress}% concluído
                            </Text>
                        </VStack>
                    </ModalBody>
                </ModalContent>
            </Modal>
            <Footer />
        </Box>
    );
};

export default AlbumCreator;